/**
 * Page publique d'une analyse partagée (accessible sans login via /p/:token).
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import DOMPurify from 'dompurify';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface PublicAnalyse {
  id: number;
  modules: string;
  contenu: string;
  createdAt: string;
  chart: {
    type: 'NATAL' | 'REVOLUTION_SOLAIRE' | 'TRANSITS' | 'PROGRESSIONS';
    anneeRs: number | null;
    client: { prenom: string; nom: string };
  };
}

interface PublicResponse {
  analyse: PublicAnalyse;
  sharedAt: string;
  expiresAt: string | null;
}

function formatModules(modules: string): string {
  switch (modules) {
    case 'tn': return 'Thème natal';
    case 'rs': return 'Révolution solaire';
    case 'prog': return 'Progressions';
    case 'tout': return 'Analyse complète';
    case 'transits': return 'Transits';
    default: return modules;
  }
}

function chartLabel(chart: PublicAnalyse['chart']): string {
  switch (chart.type) {
    case 'NATAL': return 'Thème natal';
    case 'REVOLUTION_SOLAIRE': return chart.anneeRs ? `Révolution solaire ${chart.anneeRs}` : 'Révolution solaire';
    case 'TRANSITS': return 'Transits';
    case 'PROGRESSIONS': return 'Progressions';
    default: return 'Thème';
  }
}

export function PublicAnalysePage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<PublicResponse | null>(null);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE}/public/analyses/${token}`)
      .then(async (r) => {
        const body = await r.json();
        if (r.ok && body.success) {
          setData(body.data);
        } else {
          setError(body.error ?? 'Lien invalide');
        }
      })
      .catch(() => setError('Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div style={containerStyle}>
        <p style={{ color: '#B0A898', fontSize: '0.95rem' }}>Chargement de l'analyse…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={containerStyle}>
        <div style={errorBoxStyle}>
          <h1 style={{ margin: '0 0 12px', color: '#8a6e16', fontSize: '1.4rem' }}>Lien indisponible</h1>
          <p style={{ margin: 0, color: '#3D3830' }}>{error || 'Cette analyse n\'est plus accessible.'}</p>
        </div>
      </div>
    );
  }

  const { analyse, expiresAt } = data;
  const dateStr = new Date(analyse.createdAt).toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <div style={pageBgStyle}>
      <style>{globalCss}</style>

      {/* Header / cover */}
      <header style={headerStyle}>
        <div style={{ width: 60, height: 3, background: '#c9a227', marginBottom: 24 }} />
        <h1 style={titleStyle}>Analyse astrologique</h1>
        <p style={subtitleStyle}>
          {chartLabel(analyse.chart)} · {formatModules(analyse.modules)}
        </p>
        <p style={clientStyle}>
          {analyse.chart.client.prenom} {analyse.chart.client.nom}
        </p>
        <p style={metaStyle}>{dateStr}</p>
      </header>

      {/* Toolbar imprimer */}
      <div style={toolbarStyle} className="no-print">
        <span style={{ fontSize: '0.85rem', color: '#B0A898' }}>
          Analyse partagée par Arketyp
          {expiresAt && (
            <span style={{ marginLeft: 12 }}>
              · Lien valide jusqu'au {new Date(expiresAt).toLocaleDateString('fr-FR')}
            </span>
          )}
        </span>
        <button
          onClick={() => window.print()}
          style={printButtonStyle}
        >
          <i className="bi bi-printer" style={{ marginRight: 6 }} />
          Imprimer / Enregistrer en PDF
        </button>
      </div>

      {/* Contenu */}
      <article
        className="analyse-content"
        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(analyse.contenu) }}
      />

      <footer style={footerStyle} className="no-print">
        <p style={{ margin: 0, fontSize: '0.8rem', color: '#B0A898' }}>
          Arketyp — Astrologie humaniste évolutive
        </p>
      </footer>
    </div>
  );
}

// ─── Styles ───

const pageBgStyle: React.CSSProperties = {
  background: '#fafafa',
  minHeight: '100vh',
  fontFamily: "'Inter', 'Helvetica', sans-serif",
  color: '#333333',
};

const containerStyle: React.CSSProperties = {
  ...pageBgStyle,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px 20px',
};

const errorBoxStyle: React.CSSProperties = {
  background: '#fff',
  padding: '32px 28px',
  borderRadius: 12,
  border: '1px solid #f5e6b3',
  maxWidth: 460,
  textAlign: 'center',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
};

const headerStyle: React.CSSProperties = {
  maxWidth: 720,
  margin: '0 auto',
  padding: '60px 24px 30px',
};

const titleStyle: React.CSSProperties = {
  margin: '0 0 12px',
  fontSize: '2.5rem',
  fontWeight: 700,
  color: '#8a6e16',
  lineHeight: 1.1,
  letterSpacing: '-0.5px',
};

const subtitleStyle: React.CSSProperties = {
  margin: '0 0 50px',
  fontSize: '0.95rem',
  color: '#B0A898',
  textTransform: 'uppercase',
  letterSpacing: '1px',
};

const clientStyle: React.CSSProperties = {
  margin: '0 0 4px',
  fontSize: '1.4rem',
  fontWeight: 700,
  color: '#1a1a1a',
};

const metaStyle: React.CSSProperties = {
  margin: 0,
  fontSize: '0.85rem',
  color: '#B0A898',
  fontStyle: 'italic',
};

const toolbarStyle: React.CSSProperties = {
  maxWidth: 720,
  margin: '0 auto',
  padding: '12px 24px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  background: '#fff',
  borderTop: '1px solid #f0f0f0',
  borderBottom: '1px solid #f0f0f0',
};

const printButtonStyle: React.CSSProperties = {
  background: '#c9a227',
  color: '#1a1a1a',
  border: 0,
  padding: '8px 18px',
  borderRadius: 6,
  fontSize: '0.85rem',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const footerStyle: React.CSSProperties = {
  maxWidth: 720,
  margin: '0 auto',
  padding: '40px 24px 60px',
  textAlign: 'center',
};

const globalCss = `
  body { margin: 0; }

  .analyse-content {
    max-width: 720px;
    margin: 0 auto;
    padding: 30px 24px;
    background: #fff;
    text-align: justify;
    font-size: 1rem;
    line-height: 1.7;
    color: #333;
  }

  .analyse-content h1 {
    font-size: 1.6rem;
    font-weight: 700;
    color: #8a6e16;
    margin: 1.6em 0 0.5em;
    padding-bottom: 0.3em;
    border-bottom: 1.5px solid #c9a227;
  }
  .analyse-content h2 {
    font-size: 1.25rem;
    font-weight: 700;
    color: #b8921f;
    margin: 1.3em 0 0.5em;
  }
  .analyse-content h3 {
    font-size: 1.05rem;
    font-weight: 700;
    color: #3D3830;
    margin: 1em 0 0.4em;
  }
  .analyse-content p {
    margin: 0 0 0.8em;
  }
  .analyse-content blockquote {
    margin: 1em 0;
    padding: 0.8em 1.2em;
    border-left: 4px solid #c9a227;
    background: #f8f6f0;
    color: #3D3830;
    font-style: italic;
    border-radius: 0 6px 6px 0;
  }
  .analyse-content ul, .analyse-content ol {
    margin: 0.6em 0 1em 1.5em;
    padding: 0;
  }
  .analyse-content li { margin-bottom: 0.3em; }
  .analyse-content table {
    width: 100%;
    border-collapse: collapse;
    margin: 1em 0;
    font-size: 0.95em;
  }
  .analyse-content th, .analyse-content td {
    border: 1px solid #e0e0e0;
    padding: 8px 12px;
    text-align: left;
  }
  .analyse-content th {
    background: #f5e6b3;
    color: #1a1a1a;
    font-weight: 700;
  }
  .analyse-content strong { color: #1a1a1a; font-weight: 700; }
  .analyse-content em { font-style: italic; }

  @media print {
    .no-print { display: none !important; }
    body { background: #fff !important; }
    .analyse-content { padding: 0; max-width: none; }
    @page { size: A4; margin: 18mm 16mm; }
  }
`;
